import { BarChartOutlined } from '@ant-design/icons';
import { Button, Popover } from 'antd';

import { type LeadCardUserStatsData } from '@app/components/LeadCard/data';
import { divideAmount } from '@app/utils';

import type { FC, ReactNode } from 'react';

import './UserStatsPopover.scss';

interface UserStatsPopoverProps {
  stats: LeadCardUserStatsData;
}

interface StatRow {
  key: string;
  label: string;
  value: ReactNode;
}

const formatVisitDate = (isoDate: string | null): string | null => {
  if (!isoDate) return null;

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return null;

  return date
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
    .replace(/\//g, '.');
};

export const UserStatsPopover: FC<UserStatsPopoverProps> = ({
  stats: { visitsCount, salesCount, firstVisitDate, lastVisitDate },
}) => {
  const firstVisit = formatVisitDate(firstVisitDate);
  const lastVisit = formatVisitDate(lastVisitDate);

  const rows: StatRow[] = [
    { key: 'visits', label: 'Визитов', value: visitsCount },
    { key: 'sales', label: 'Продано', value: divideAmount(salesCount) },
    ...(firstVisit
      ? [{ key: 'first-visit', label: 'Первый визит', value: firstVisit }]
      : []),
    ...(lastVisit
      ? [{ key: 'last-visit', label: 'Последний визит', value: lastVisit }]
      : []),
  ];

  const content = (
    <ul className="UserStatsPopover__list">
      {rows.map(({ key, label, value }) => (
        <li key={key} className="UserStatsPopover__row">
          <span className="UserStatsPopover__label">{label}</span>
          <span className="UserStatsPopover__value">{value}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <Popover
      content={content}
      title="Статистика"
      trigger={['hover', 'click']}
      classNames={{
        root: 'UserStatsPopover__overlay',
      }}
      placement="left"
    >
      <Button
        className="UserStatsPopover__trigger"
        type="text"
        size="small"
        icon={<BarChartOutlined />}
        aria-label="Статистика"
      />
    </Popover>
  );
};
